import { ClipboardEvent, KeyboardEvent, useEffect, useRef } from 'react';

type OtpInputProps = {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  testIdPrefix: string;
  onComplete?: () => void;
};

export const OtpInput = ({
  value,
  onChange,
  length = 6,
  disabled = false,
  testIdPrefix,
  onComplete
}: OtpInputProps) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const hasFiredCompleteRef = useRef(false);

  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  useEffect(() => {
    if (value.length === length) {
      if (!hasFiredCompleteRef.current) {
        hasFiredCompleteRef.current = true;
        onComplete?.();
      }
    } else {
      hasFiredCompleteRef.current = false;
    }
  }, [value, length, onComplete]);

  const handleChange = (index: number, char: string) => {
    if (!/^\d*$/.test(char)) {
      return;
    }
    const newDigits = [...digits];
    newDigits[index] = char.slice(-1);
    onChange(newDigits.join(''));
    if (char && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const newDigits = [...digits];
        newDigits[index] = '';
        onChange(newDigits.join(''));
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
        const newDigits = [...digits];
        newDigits[index - 1] = '';
        onChange(newDigits.join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted.padEnd(length, '').slice(0, length).replace(/\s/g, ''));
    onChange(pasted);
    const focusIndex = Math.min(pasted.length, length - 1);
    inputsRef.current[focusIndex]?.focus();
  };

  return (
    <div className='flex gap-2'>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputsRef.current[index] = el)}
          data-testid={`${testIdPrefix}-${index}`}
          type='text'
          inputMode='numeric'
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className='h-12 w-10 rounded-lg border border-secondary-blue-gray text-center text-xl font-semibold tracking-widest focus:border-primary-blue focus:outline-none focus:ring-2 focus:ring-primary-blue disabled:opacity-50'
        />
      ))}
    </div>
  );
};
